import {
  IndexTable,
  Layout,
  LegacyCard,
  EmptySearchResult,
  Banner,
} from "@shopify/polaris";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { processesApi } from "../../../redux/troubleshooting/processes/Action";
import { useNavigate, useParams } from "react-router-dom";
import { HistoricalSkeletonTable } from "../../../components/skeletonLoader";
import SupportBackInTime from "./SupportBackInTime";
import {
  displayBoolean,
  displayCount,
  displayDateTime,
  displayText,
  ReadOnlyTablePagination,
  SUPPORT_TABLE_PAGE_SIZE,
} from "./readOnlyTable";

const Processes = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeShopId = String(id ?? "");
  const [pagination, setPagination] = useState({
    shopId: routeShopId,
    pageNumber: 0,
  });
  const pageNumber =
    pagination.shopId === routeShopId ? pagination.pageNumber : 0;

  const dispatch = useDispatch();
  const processesData = useSelector((store) => store.requiredProcessesData);
  const isCurrentStore = processesData.shopId === String(id);
  const processes =
    isCurrentStore &&
    processesData.isSuccess &&
    Array.isArray(processesData.data?.content)
      ? processesData.data.content
      : [];
  const readError =
    isCurrentStore && processesData.isError ? processesData.error : null;
  const isLoading = !isCurrentStore || processesData.isRequest;

  useEffect(() => {
    const payload = {
      storeId: id,
      pageNumber,
      pageSize: SUPPORT_TABLE_PAGE_SIZE,
    };
    dispatch(processesApi(payload));
  }, [dispatch, id, pageNumber]);

  useEffect(() => {
    if (
      isCurrentStore &&
      (processesData?.data?.message === "Actual error message" ||
        processesData.error === "Actual error message")
    ) {
      navigate("/logout");
    }
  }, [isCurrentStore, navigate, processesData.data, processesData.error]);


  const resourceName = {
    singular: "process",
    plural: "processes",
  };

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No processes found"
      description="No historical support processes were returned for this page."
      withIllustration
    />
  );

  const rowMarkup = processes?.map(
    (
      {
        storeId,
        backInTimeIdentifier,
        status,
        fromDate,
        endDate,
        total,
        pending,
        grandTotal,
        missingTracker,
        missingTransaction,
        dateFieldName,
        pullingState,
        creationDate,
        backInTimeType,
      },
      index
    ) => (
      <IndexTable.Row
        id={backInTimeIdentifier || `${storeId}-${index}`}
        key={backInTimeIdentifier || `${storeId}-${index}`}
        position={index}
      >
        <IndexTable.Cell>{displayText(backInTimeIdentifier)}</IndexTable.Cell>
        <IndexTable.Cell>
          <p>{displayText(backInTimeType)}</p>
          <p className="secondaryText">
            {displayBoolean(pullingState, "Pulling", "Not pulling")}
          </p>
        </IndexTable.Cell>
        <IndexTable.Cell>{displayText(status)}</IndexTable.Cell>
        <IndexTable.Cell>
          <p>
            {displayDateTime(fromDate, { includeTime: false })} –{" "}
            {displayDateTime(endDate, { includeTime: false })}
          </p>
          <p className="secondaryText">
            Date field: {displayText(dateFieldName)}
          </p>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <p>Total: {displayCount(total)}</p>
          <p>Pending: {displayCount(pending)}</p>
          <p>Grand total: {displayCount(grandTotal)}</p>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <p>Trackers: {displayCount(missingTracker)}</p>
          <p>Transactions: {displayCount(missingTransaction)}</p>
        </IndexTable.Cell>
        <IndexTable.Cell>{displayDateTime(creationDate)}</IndexTable.Cell>
      </IndexTable.Row>
    )
  );

  const backInTime = (
    <span className="d-flex gap-10">
      <span>Back in time ID</span>
    </span>
  );
  const type = (
    <span className="d-flex gap-10">
      <span>Type</span>
    </span>
  );
  const status = (
    <span className="d-flex gap-10">
      <span>Status</span>
    </span>
  );
  const handlePageChange = (nextPage) =>
    setPagination({ shopId: routeShopId, pageNumber: nextPage });

  return (
    <>
      <Layout.Section>
        <div className="wrapper">
          <LegacyCard sectioned>
            <p className="card_title" style={{ padding: "15px" }}>
              Back in time support
            </p>
            <div
              className="d-flex gap-10"
              style={{ alignItems: "center", padding: "15px" }}
            >
              <div>
                <SupportBackInTime />
              </div>
            </div>
          </LegacyCard>
        </div>
      </Layout.Section>
      <Layout.Section>
        <div className="wrapper">
          <LegacyCard sectioned>
            <p className="card_title" style={{ padding: "15px" }}>
              Processes
            </p>
            <LegacyCard sectioned>
              {isLoading ? (
                <HistoricalSkeletonTable />
              ) : readError ? (
                <Banner title="Processes could not be loaded" status="critical">
                  <p>{readError}</p>
                </Banner>
              ) : (
                <IndexTable
                  resourceName={resourceName}
                  itemCount={processes?.length || 0}
                  emptyState={emptyStateMarkup}
                  headings={[
                    { id: "1", title: backInTime },
                    { id: "7", title: type },
                    { id: "2", title: status },
                    { id: "3", title: "Date range" },
                    { id: "4", title: "Progress" },
                    { id: "5", title: "Missing data" },
                    { id: "6", title: "Created" },
                  ]}
                  selectable={false}
                >
                  {rowMarkup}
                </IndexTable>
              )}
              {!isLoading && !readError ? (
                <ReadOnlyTablePagination
                  response={processesData.data}
                  currentPage={pageNumber}
                  rowCount={processes.length}
                  noun="process"
                  pluralNoun="processes"
                  onPageChange={handlePageChange}
                />
              ) : null}
            </LegacyCard>
          </LegacyCard>
        </div>
      </Layout.Section>
    </>
  );
};

export default Processes;
